import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { Skeleton } from "../../Components/Needed";
import { ConfirmDialog, EmptyState, SidePanel, StatusChip } from "../../Components/Kit";
import { deleteMedia, fetchMedia, fetchMediaDetails, fetchMediaItem, replaceMedia, saveMediaItem, uploadMedia } from "../../Helpers/notificationConsoleApi";
import { whenText } from "./shared";

// Notification Centre > Media (R1.3): images, videos and PDFs uploaded once and used by templates.
// Each image keeps one crop per use (push 2:1, WhatsApp 1.91:1, email 2:1); a file a template uses
// can't be deleted, and "Replace file" swaps it everywhere at once.
const KINDS = [{ key: "", label: "All" }, { key: "image", label: "Images" }, { key: "video", label: "Videos" }, { key: "document", label: "PDFs" }];
const USES = [{ key: "push", label: "Push", ratio: 2 }, { key: "whatsapp", label: "WhatsApp header", ratio: 1.91 }, { key: "email", label: "Email header", ratio: 2 }];
const CHANNEL = { push: "Push", email: "Email", whatsapp: "WhatsApp" };
const ACCEPT = "image/jpeg,image/png,image/webp,video/mp4,video/3gpp,application/pdf";

// The largest crop of a shape at zoom 1, as fractions of the picture.
const baseCrop = (asset, ratio) => {
    if (!asset.width || !asset.height) return { w: 1, h: 1 };
    return asset.width / asset.height > ratio ? { w: (asset.height * ratio) / asset.width, h: 1 } : { w: 1, h: (asset.width / ratio) / asset.height };
};
const toControls = (asset, ratio, crop) => {
    const base = baseCrop(asset, ratio);
    const c = crop || { x: (1 - base.w) / 2, y: (1 - base.h) / 2, w: base.w, h: base.h };
    return { zoom: Math.min(3, Math.max(1, base.w / c.w)), px: c.w < 1 ? c.x / (1 - c.w) : 0.5, py: c.h < 1 ? c.y / (1 - c.h) : 0.5 };
};
const toCrop = (asset, ratio, { zoom, px, py }) => {
    const base = baseCrop(asset, ratio);
    const w = base.w / zoom;
    const h = base.h / zoom;
    const r4 = (n) => Math.round(n * 10000) / 10000;
    return { x: r4(px * (1 - w)), y: r4(py * (1 - h)), w: r4(w), h: r4(h) };
};
const cropStyle = (url, crop, ratio) => ({
    aspectRatio: String(ratio),
    backgroundImage: `url("${url}")`,
    backgroundSize: `${100 / crop.w}% ${100 / crop.h}%`,
    backgroundPosition: `${crop.w < 1 ? (crop.x / (1 - crop.w)) * 100 : 0}% ${crop.h < 1 ? (crop.y / (1 - crop.h)) * 100 : 0}%`,
    backgroundRepeat: "no-repeat",
});

const CropEditor = ({ asset, use, crop, onChange }) => {
    const c = toControls(asset, use.ratio, crop);
    const set = (patch) => onChange(toCrop(asset, use.ratio, { ...c, ...patch }));
    const current = crop || toCrop(asset, use.ratio, c);
    return (
        <div className="md-crop">
            <div className="md-crop-head"><span>{use.label} <span className="kit-sub">({use.ratio}:1)</span></span></div>
            <div className="md-crop-box" style={cropStyle(asset.url, current, use.ratio)} role="img" aria-label={`${use.label} crop`} />
            <div className="md-crop-controls">
                <label>Zoom<input type="range" min="1" max="3" step="0.05" value={c.zoom} onChange={(e) => set({ zoom: Number(e.target.value) })} /></label>
                <label>Left / right<input type="range" min="0" max="1" step="0.01" value={c.px} onChange={(e) => set({ px: Number(e.target.value) })} disabled={current.w >= 0.999} /></label>
                <label>Up / down<input type="range" min="0" max="1" step="0.01" value={c.py} onChange={(e) => set({ py: Number(e.target.value) })} disabled={current.h >= 0.999} /></label>
            </div>
        </div>
    );
};

const Thumb = ({ asset }) => {
    if (asset.kind === "image") return <img src={asset.links?.push || asset.url} alt={asset.alt || ""} className="md-thumb-img" loading="lazy" />;
    if (asset.kind === "video") return <div className="md-thumb-ph md-thumb-video" aria-label="Video">▶</div>;
    return <div className="md-thumb-ph" aria-label="PDF">PDF</div>;
};

const MediaTab = () => {
    const [items, setItems] = useState(null);
    const [details, setDetails] = useState([]);
    const [kind, setKind] = useState("");
    const [search, setSearch] = useState("");
    const [selected, setSelected] = useState(null); // full asset
    const [form, setForm] = useState(null);         // { name, alt, crops }
    const [busy, setBusy] = useState("");
    const [confirm, setConfirm] = useState(false);
    const [dragging, setDragging] = useState(false);
    const uploadRef = useRef(null);
    const replaceRef = useRef(null);

    const load = useCallback(async () => {
        const result = await fetchMedia(kind ? { kind } : {});
        if (result.ok) setItems(result.data.items || []); else { toast.error(result.message); setItems([]); }
    }, [kind]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => { fetchMediaDetails().then((r) => { if (r.ok) setDetails(r.data.details || []); }); }, []);

    const open = async (mediaId) => {
        const result = await fetchMediaItem(mediaId);
        if (!result.ok) return toast.error(result.message);
        const asset = result.data.asset;
        setSelected(asset);
        setForm({ name: asset.name, alt: asset.alt || "", crops: { ...(asset.crops || {}) } });
        return null;
    };
    const shown = useMemo(() => {
        const term = search.trim().toLowerCase();
        return (items || []).filter((a) => !term || `${a.name} ${a.alt || ""}`.toLowerCase().includes(term));
    }, [items, search]);

    const doUpload = async (files) => {
        const list = Array.from(files || []);
        if (!list.length) return;
        setBusy("upload");
        let last = null;
        for (const file of list) {
            // eslint-disable-next-line no-await-in-loop
            const result = await uploadMedia(file, { name: file.name });
            if (!result.ok) toast.error(`${file.name}: ${result.message}`); else last = result.data.asset;
        }
        setBusy("");
        if (uploadRef.current) uploadRef.current.value = "";
        await load();
        if (last) { toast.success(list.length > 1 ? "Files uploaded" : "Uploaded"); open(last.mediaId); }
    };
    const save = async () => {
        setBusy("save");
        const result = await saveMediaItem({ mediaId: selected.mediaId, name: form.name, alt: form.alt, ...(selected.kind === "image" ? { crops: form.crops } : {}) });
        setBusy("");
        if (!result.ok) return toast.error(result.message);
        toast.success("Saved. Templates use the new crops within about 30 seconds.");
        setSelected(result.data.asset);
        return load();
    };
    const doReplace = async (file) => {
        if (!file) return;
        setBusy("replace");
        const result = await replaceMedia(selected.mediaId, file);
        setBusy("");
        if (replaceRef.current) replaceRef.current.value = "";
        if (!result.ok) return toast.error(result.message);
        toast.success("File replaced everywhere it is used");
        setSelected(result.data.asset);
        setForm({ name: result.data.asset.name, alt: result.data.asset.alt || "", crops: { ...(result.data.asset.crops || {}) } });
        return load();
    };
    const doDelete = async () => {
        setBusy("delete");
        const result = await deleteMedia(selected.mediaId);
        setBusy("");
        setConfirm(false);
        if (!result.ok) return toast.error(result.message);
        toast.success("Deleted");
        setSelected(null);
        return load();
    };

    const dirty = selected && form && (form.name !== selected.name || form.alt !== (selected.alt || "") || JSON.stringify(form.crops) !== JSON.stringify(selected.crops || {}));

    return (
        <div className="settings-card">
            <div className="md-head">
                <div>
                    <h5 className="fw-semibold mb-1">Media</h5>
                    <p className="text-muted small mb-0">Pictures, videos and PDFs for templates. Upload once, use anywhere; replacing a file updates every template that uses it.</p>
                </div>
                <button type="button" className="kit-btn kit-btn-primary" onClick={() => uploadRef.current?.click()} disabled={busy === "upload"}>{busy === "upload" ? "Uploading..." : "Upload"}</button>
                <input ref={uploadRef} type="file" accept={ACCEPT} multiple hidden onChange={(e) => doUpload(e.target.files)} />
            </div>

            <div className="md-layout">
                <div className="md-main">
                    <div
                        className={`md-drop ${dragging ? "is-over" : ""}`}
                        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                        onDragLeave={() => setDragging(false)}
                        onDrop={(e) => { e.preventDefault(); setDragging(false); doUpload(e.dataTransfer.files); }}
                    >
                        <div className="fw-bold">Drop files here, or <button type="button" className="md-linkbtn" onClick={() => uploadRef.current?.click()}>choose files</button></div>
                        <div className="kit-sub">Images JPG / PNG / WebP up to 5 MB · Videos MP4 up to 16 MB · PDFs up to 25 MB</div>
                    </div>

                    <div className="md-filters">
                        <div className="md-seg" role="group" aria-label="Kind">
                            {KINDS.map((k) => <button key={k.key} type="button" className={kind === k.key ? "on" : ""} onClick={() => setKind(k.key)}>{k.label}</button>)}
                        </div>
                        <input type="search" className="form-control form-control-sm md-search" placeholder="Search files" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search files" />
                    </div>

                    {items === null ? <Skeleton height={220} /> : shown.length === 0 ? (
                        <EmptyState title={(items || []).length ? "No files match" : "No files yet"} text={(items || []).length ? "Try another search." : "Upload a picture for your first offer or booking message."} />
                    ) : (
                        <div className="md-grid">
                            {shown.map((a) => (
                                <button key={a.mediaId} type="button" className={`md-tile ${selected?.mediaId === a.mediaId ? "is-on" : ""}`} onClick={() => open(a.mediaId)}>
                                    <Thumb asset={a} />
                                    <span className="md-tile-text">
                                        <span className="md-tile-name">{a.name}</span>
                                        <span className="kit-sub">{a.kind === "document" ? "PDF" : a.kind === "video" ? "Video" : "Image"} · {a.sizeText} · {a.usedCount ? `used by ${a.usedCount}` : "not used"}</span>
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}

                    {details.length > 0 && (
                        <div className="md-details">
                            <div className="fw-bold mb-1">Media that changes with each message</div>
                            <div className="kit-sub mb-2">Choose these in a template instead of a fixed file. If one is missing for a booking, the template's fixed file is used.</div>
                            <div className="md-details-list">
                                {details.map((d) => (
                                    <div key={d.name} className="md-detail"><code>{`{${d.name}}`}</code><div className="kit-sub">{d.label}{d.kind === "location" ? " · for a WhatsApp location header" : ""}</div></div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <SidePanel open={!!selected && !!form} title={selected?.name || ""} subtitle={selected ? `${selected.kind === "document" ? "PDF" : selected.kind === "video" ? "Video" : "Image"}${selected.width ? ` · ${selected.width} × ${selected.height}` : ""} · ${selected.sizeText} · ${whenText(selected.createdAt)}` : ""} onClose={() => setSelected(null)} label="Selected file"
                    footer={selected ? (
                        <>
                            <button type="button" className="kit-btn kit-btn-primary" onClick={save} disabled={!dirty || !!busy}>{busy === "save" ? "Saving..." : "Save"}</button>
                            <button type="button" className="kit-btn kit-btn-outline" onClick={() => replaceRef.current?.click()} disabled={!!busy}>{busy === "replace" ? "Replacing..." : "Replace file"}</button>
                            <button type="button" className="kit-btn kit-btn-outline" onClick={() => setConfirm(true)} disabled={!!busy || selected.usedCount > 0} title={selected.usedCount > 0 ? "In use, so it can't be deleted" : ""} style={{ color: "#B42318" }}>Delete</button>
                            <input ref={replaceRef} type="file" accept={ACCEPT} hidden onChange={(e) => doReplace(e.target.files?.[0])} />
                        </>
                    ) : null}
                >
                    {selected && form ? (
                        <>
                            <label className="md-field">Name<input className="form-control form-control-sm" value={form.name} maxLength={120} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
                            {selected.kind === "image" ? <label className="md-field">Alt text <span className="kit-sub">(read out when images are blocked)</span><input className="form-control form-control-sm" value={form.alt} maxLength={200} onChange={(e) => setForm({ ...form, alt: e.target.value })} /></label> : null}
                            {selected.kind === "image" ? (
                                <div>
                                    <div className="md-label">CROP FOR EACH USE</div>
                                    {USES.map((use) => <CropEditor key={use.key} asset={selected} use={use} crop={form.crops?.[use.key]} onChange={(crop) => setForm({ ...form, crops: { ...form.crops, [use.key]: crop } })} />)}
                                </div>
                            ) : (
                                <a href={selected.url} target="_blank" rel="noreferrer" className="kit-btn kit-btn-outline" style={{ alignSelf: "flex-start" }}>Open the file</a>
                            )}
                            <div>
                                <div className="md-label">WHERE IT CAN BE USED</div>
                                <div className="md-fit">
                                    {Object.entries(selected.fit || {}).map(([ch, f]) => (
                                        <div key={ch} className="md-fit-row"><StatusChip tone={f.ok ? (f.reason ? "wait" : "ok") : "neutral"}>{CHANNEL[ch]}</StatusChip><span className="kit-sub">{f.ok ? (f.reason || "Fits") : f.reason}</span></div>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <div className="md-label">USED BY</div>
                                {selected.usedBy?.length ? selected.usedBy.map((u) => <div key={u.libraryId} className="small">{u.name} <span className="kit-sub">({CHANNEL[u.channel] || u.channel})</span></div>) : <div className="kit-sub">No template uses it yet.</div>}
                            </div>
                        </>
                    ) : null}
                </SidePanel>
            </div>

            <ConfirmDialog open={confirm} title={`Delete ${selected?.name || "this file"}?`} text="It is removed from the library and from Cloudinary. This can't be undone." confirmLabel="Delete" danger busy={busy === "delete"} onConfirm={doDelete} onCancel={() => setConfirm(false)} />
        </div>
    );
};

export default MediaTab;
